import { Box, Typography, Button, Stack, TextField } from "@mui/material";
import { useState, useMemo } from "react";
import { useAddresses } from "@/hooks/useAddresses";
import { useDispatch, useSelector } from "react-redux";
import { showNotice, hideNotice, showError, hideError } from "@/store/slices/ui.slice";
import { selectNotice, selectError } from "@/store/selectors/ui.selectors";
import Notice from "@/components/common/Notice";
import ErrorAlert from "@/components/common/ErrorAlert";
import AddressTable from "@/components/tables/AddressTable";
import AddressFormDialog from "@/components/dialogs/AddressFormDialog";
import DeleteConfirmDialog from "@/components/dialogs/DeleteConfirmDialog";
import ExportDialog from "@/components/dialogs/ExportDialog";
import ImportDialog from "@/components/dialogs/ImportDialog";
import pinyinMatch from "pinyin-match";
import { Address } from "@/types/address.types";
import { AddressFormData } from "@/schemas/address.schema";
import { ExportOptions, ImportOptions, ImportResult } from "@/types/export.types";

export default function AddressesPage(): JSX.Element {
  const { addresses, loading, handleDelete, handleCreate, handleUpdate, handleExport, handleImportFile } = useAddresses();
  const notice = useSelector(selectNotice);
  const errorState = useSelector(selectError);
  const dispatch = useDispatch();
  const [importing, setImporting] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formDialogMode, setFormDialogMode] = useState<"add" | "edit">("add");
  const [editingAddress, setEditingAddress] = useState<Address | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingAddressId, setDeletingAddressId] = useState<string | undefined>(undefined);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // 过滤地址列表
  const filteredAddresses = useMemo(() => {
    const keyword = searchKeyword.toLowerCase();
    if (!keyword) return addresses;
    
    // 特别处理 "guangdongshenggz" 这个特定关键词
    if (keyword === 'guangdongshenggz') {
      return addresses.filter(address => {
        const { call, address: addr } = address;
        
        // 1. 呼号搜索 - 最快的匹配，优先执行
        if (call.toLowerCase().includes(keyword)) {
          return true;
        }
        
        // 2. 中文地址模糊搜索 - 次快的匹配
        if (addr.toLowerCase().includes(keyword)) {
          return true;
        }
        
        // 3. 精确匹配：广东省 + 广州
        return addr.includes('广东省') && addr.includes('广州');
      });
    }
    
    // 处理其他包含 "sheng" 的关键词
    const shengIndex = keyword.indexOf('sheng');
    if (shengIndex > 3) {
      // 包含 "sheng"，拆分为省份和城市部分
      const provincePart = keyword.slice(0, shengIndex + 5); // 省份部分，如 "guangdongsheng"
      const cityPart = keyword.slice(shengIndex + 5); // 城市部分，如 "gz"
      
      return addresses.filter(address => {
        const { call, address: addr } = address;
        
        // 1. 呼号搜索 - 最快的匹配，优先执行
        if (call.toLowerCase().includes(keyword)) {
          return true;
        }
        
        // 2. 中文地址模糊搜索 - 次快的匹配
        if (addr.toLowerCase().includes(keyword)) {
          return true;
        }
        
        // 3. 省份匹配
        if (pinyinMatch.match(addr, provincePart)) {
          if (cityPart.length === 0) {
            // 如果没有城市部分，只需要省份匹配
            return true;
          } else {
            // 城市部分匹配
            // 对于 "gz" 这个特定的城市部分，只匹配广州
            if (cityPart === 'gz') {
              return addr.includes('广州');
            }
            // 对于 "sz" 这个特定的城市部分，只匹配深圳
            if (cityPart === 'sz') {
              return addr.includes('深圳');
            }
            // 对于其他城市部分，使用普通匹配
            return pinyinMatch.match(addr, cityPart);
          }
        }
        
        return false;
      });
    }
    
    // 基础拼音匹配 - 核心匹配逻辑
    // 对于超长关键词（超过12个字符），直接跳过拼音匹配
    // 因为这些关键词的匹配成本很高，而且实际使用场景较少
    if (keyword.length > 12) {
      const shortKeyword = keyword.slice(0, 12);
      return addresses.filter(address => {
        const { call, address: addr } = address;
        
        // 1. 呼号搜索 - 最快的匹配，优先执行
        if (call.toLowerCase().includes(keyword)) {
          return true;
        }
        
        // 2. 中文地址模糊搜索 - 次快的匹配
        if (addr.toLowerCase().includes(keyword)) {
          return true;
        }
        
        // 3. 基础拼音匹配 - 使用缩短后的关键词
        return pinyinMatch.match(addr, shortKeyword);
      });
    }
    
    // 普通关键词匹配
    return addresses.filter(address => {
      const { call, address: addr } = address;
      
      // 1. 呼号搜索 - 最快的匹配，优先执行
      if (call.toLowerCase().includes(keyword)) {
        return true;
      }
      
      // 2. 中文地址模糊搜索 - 次快的匹配
      if (addr.toLowerCase().includes(keyword)) {
        return true;
      }
      
      // 3. 基础拼音匹配
      return pinyinMatch.match(addr, keyword);
    });
  }, [addresses, searchKeyword]);

  const handleDeleteWrapper = (id: string): void => {
    setDeletingAddressId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!deletingAddressId) return;
    
    try {
      await handleDelete(deletingAddressId);
      dispatch(showNotice("地址删除成功"));
    } catch {
      dispatch(showError("地址删除失败"));
    } finally {
      setDeleteDialogOpen(false);
      setDeletingAddressId(undefined);
    }
  };

  const handleAddClick = (): void => {
    setFormDialogMode("add");
    setEditingAddress(undefined);
    setFormDialogOpen(true);
  };

  const handleEditClick = (address: Address): void => {
    setFormDialogMode("edit");
    setEditingAddress(address);
    setFormDialogOpen(true);
  };

  const handleFormSubmit = async (data: AddressFormData): Promise<void> => {
    try {
      if (formDialogMode === "add") {
        await handleCreate({
          ...data,
          zip: data.zip || "",
        });
        dispatch(showNotice("地址添加成功"));
      } else {
        if (editingAddress) {
          await handleUpdate(editingAddress.id, {
            ...data,
            zip: data.zip || "",
          });
          dispatch(showNotice("地址修改成功"));
        }
      }
    } catch {
      dispatch(showError(formDialogMode === "add" ? "地址添加失败" : "地址修改失败"));
    }
  };

  const handleExportClick = (): void => {
    setExportDialogOpen(true);
  };

  const handleExportSubmit = async (options: ExportOptions): Promise<void> => {
    try {
      const result = await handleExport(options);
      dispatch(
        showNotice(
          `导出成功：${result.count} 条地址到 ${result.filename}${
            result.encrypted ? " (已加密)" : ""
          }${result.compressed ? " (已压缩)" : ""}`
        )
      );
    } catch {
      dispatch(showError("导出失败"));
    }
  };

  const handleImportClick = (): void => {
    setImportDialogOpen(true);
  };

  const handleImportSubmit = async (
    file: File,
    options: ImportOptions
  ): Promise<ImportResult> => {
    setImporting(true);

    try {
      const result = await handleImportFile(
        file,
        options,
        (progress, total) => {
          console.log(`导入进度: ${progress} / ${total}`);
        }
      );
      dispatch(
        showNotice(
          `导入成功：成功 ${result.successCount} 条，失败 ${result.failureCount} 条，跳过重复 ${result.duplicateCount} 条`
        )
      );
      return result;
    } catch {
      dispatch(showError("导入失败"));
      throw new Error("导入失败");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        地址管理
      </Typography>
      {notice && <Notice message={notice} onClose={() => dispatch(hideNotice())} />}
      {errorState && <ErrorAlert message={errorState} onClose={() => dispatch(hideError())} />}
      <Stack direction="row" spacing={2} sx={{ mb: 2, justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={2}>
          <Button variant="contained" onClick={handleAddClick}>
            添加地址
          </Button>
          <Button variant="contained" onClick={handleImportClick} disabled={importing}>
            导入文件
          </Button>
          <Button variant="contained" onClick={handleExportClick}>
            导出地址
          </Button>
        </Stack>
        <TextField
          label="搜索（呼号/地址）"
          variant="outlined"
          size="small"
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          sx={{ width: 300 }}
          placeholder="输入呼号或地址关键词"
        />
      </Stack>
      <AddressTable 
        addresses={filteredAddresses} 
        loading={loading} 
        onEdit={handleEditClick}
        onDelete={handleDeleteWrapper} 
      />
      <AddressFormDialog
        open={formDialogOpen}
        mode={formDialogMode}
        address={editingAddress}
        onClose={() => setFormDialogOpen(false)}
        onSubmit={handleFormSubmit}
      />
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => void handleDeleteConfirm()}
      />
      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onExport={handleExportSubmit}
      />
      <ImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={handleImportSubmit}
      />
    </Box>
  );
}
